import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router';
import * as DM from '@radix-ui/react-dropdown-menu';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ChartColumn,
  ChevronDown,
  Download,
  Pin,
  PinOff,
  LogOut,
  PartyPopper,
  Puzzle,
  Search,
  Settings,
  Users,
} from 'lucide-react';
import { TeaPlusLogo } from '../brand';
import { usePartyMenuItem } from '../party/party-dock';
import { ProfileAvatar } from '../profile/avatar';
import { NotificationBell } from '../social/notification-bell';
import { clearCache, useActiveProfile } from '@/lib/queries';
import { useWindowControls } from '@/lib/desktop';
import { useCanSocialise, useSocialCounts, useUnseenActivity } from '@/lib/social';
import { api } from '@/lib/api';
import { stopPlayer } from '@/lib/mini-player';
import { useUi } from '@/lib/store';
import { cn } from '@/lib/cn';
import { Kbd } from '../ui/misc';

export const NAV = [
  { to: '/home', label: 'Home' },
  { to: '/movies', label: 'Movies' },
  { to: '/tv', label: 'TV Shows' },
  { to: '/discover', label: 'Discover' },
  { to: '/library', label: 'My Library' },
];

export function useSignOut() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return async () => {
    useUi.getState().setLookDraft(null);
    await stopPlayer();
    await api('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    clearCache(qc);
    navigate('/login', { replace: true });
  };
}

export function useSwitchProfile() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return async () => {
    useUi.getState().setLookDraft(null);
    await stopPlayer();
    await api('/api/profiles/deselect', { method: 'POST' });
    await qc.invalidateQueries({ queryKey: ['session'] });
    navigate('/profiles');
  };
}

export function AppHeader() {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { data } = useActiveProfile();
  const signOut = useSignOut();
  const switchProfile = useSwitchProfile();
  const profile = data?.profile;
  const location = useLocation();
  const win = useWindowControls();
  const social = useCanSocialise();
  const counts = useSocialCounts().data;
  const unseenActivity = useUnseenActivity();
  const party = usePartyMenuItem();
  const inParty = !!party?.members;
  const socialBadge =
    (counts?.requests ?? 0) +
    (counts?.recommendations ?? 0) +
    (counts?.messages ?? 0) +
    unseenActivity;
  const canGoBack =
    location.pathname !== '/home' &&
    ((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName)) return;
      if (e.altKey && e.key === 'ArrowLeft') {
        e.preventDefault();
        navigate(-1);
        return;
      }
      if (e.key === '/' || ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k')) {
        e.preventDefault();
        navigate('/search');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  return (
    <header
      className={cn(
        'fixed left-0 right-[var(--tp-dock-w,0px)] top-0 z-40 transition-[background,box-shadow,backdrop-filter] duration-300',
        scrolled
          ? 'glass shadow-[0_1px_0_0_var(--color-line)]'
          : 'bg-gradient-to-b from-black/70 via-black/25 to-transparent',
      )}
    >
      <div className="caption-safe mx-auto flex h-16 max-w-[1600px] items-center gap-6 px-4 sm:px-10 lg:h-[72px]">
        {canGoBack && (
          <button
            onClick={() => navigate(-1)}
            aria-label="Go back"
            title="Back (Alt+←)"
            className="-mr-3 grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white/6 text-text/85 transition-colors hover:bg-white/12 hover:text-text"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        <Link to="/home" className="shrink-0 rounded-lg" aria-label="Tea+ home">
          <TeaPlusLogo />
        </Link>

        <nav aria-label="Primary" className="hidden lg:block">
          <ul className="flex items-center gap-1">
            {NAV.map((n) => (
              <li key={n.to}>
                <NavLink
                  to={n.to}
                  className={({ isActive }) =>
                    cn(
                      'relative rounded-lg px-3 py-2 text-[15px] font-medium transition-colors',
                      isActive ? 'text-text' : 'text-text/65 hover:text-text',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {n.label}
                      <span
                        className={cn(
                          'absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full bg-pink transition-transform duration-300 origin-left',
                          isActive ? 'scale-x-100' : 'scale-x-0',
                        )}
                      />
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {win && (
            <button
              onClick={() => void win.setPinned(!win.pinned)}
              aria-label={win.pinned ? 'Stop keeping Tea+ on top' : 'Keep Tea+ on top'}
              aria-pressed={win.pinned}
              title={win.pinned ? 'Pinned on top, click to unpin' : 'Keep on top of other windows'}
              className={cn(
                'grid h-10 w-10 place-items-center rounded-full transition-colors',
                win.pinned
                  ? 'bg-pink/15 text-pink'
                  : 'text-text/70 hover:bg-white/8 hover:text-text',
              )}
            >
              {win.pinned ? <PinOff className="h-4.5 w-4.5" /> : <Pin className="h-4.5 w-4.5" />}
            </button>
          )}
          <Link
            to="/search"
            className="hidden h-10 items-center gap-2.5 rounded-full border border-white/10 bg-white/5 pl-3.5 pr-2 text-sm text-muted transition-colors hover:border-pink/40 hover:text-text sm:flex"
          >
            <Search className="h-4 w-4" />
            <span className="pr-6">Search</span>
            <Kbd>/</Kbd>
          </Link>
          <Link
            to="/search"
            className="grid h-10 w-10 place-items-center rounded-full text-text/80 sm:hidden"
            aria-label="Search"
          >
            <Search className="h-5 w-5" />
          </Link>
          <NotificationBell />
          {social && (
            <Link
              to="/friends"
              aria-label={socialBadge ? `Friends, ${socialBadge} new` : 'Friends'}
              title="Friends"
              className="relative grid h-10 w-10 place-items-center rounded-full text-text/75 transition-colors hover:bg-white/8 hover:text-text"
            >
              <Users className="h-5 w-5" />
              {socialBadge > 0 && (
                <span
                  data-testid="friends-badge"
                  className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-[#e5484d] px-1 text-[11px] font-bold leading-none text-white shadow-md ring-2 ring-bg"
                >
                  {socialBadge > 9 ? '9+' : socialBadge}
                </span>
              )}
            </Link>
          )}

          <DM.Root>
            <DM.Trigger
              className="group flex items-center gap-2 rounded-xl p-1 pr-1.5 outline-none transition-colors hover:bg-white/6 data-[state=open]:bg-white/8"
              aria-label={inParty ? 'Profile menu, in a party' : 'Profile menu'}
              data-tip="Profile menu"
            >
              <span className="relative">
                {profile ? (
                  <ProfileAvatar avatar={profile.avatar} image={profile.avatarUrl} size={36} />
                ) : (
                  <span className="block h-9 w-9 rounded-xl bg-card" />
                )}
                {inParty && (
                  <span
                    data-testid="party-badge"
                    className="absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full bg-pink text-on-pink ring-2 ring-bg"
                  >
                    <PartyPopper className="h-3 w-3" />
                  </span>
                )}
              </span>
              <ChevronDown className="hidden h-4 w-4 text-muted transition-transform group-data-[state=open]:rotate-180 sm:block" />
            </DM.Trigger>
            <DM.Portal>
              <DM.Content
                align="end"
                sideOffset={10}
                className="z-50 w-64 origin-top-right rounded-2xl border border-line bg-bg-2/95 p-2 shadow-2xl shadow-black/60 backdrop-blur-xl data-[state=open]:animate-[dialog-in_200ms_var(--ease-out-soft)]"
              >
                {profile && (
                  <div className="flex items-center gap-3 px-2.5 pb-3 pt-2">
                    <ProfileAvatar avatar={profile.avatar} image={profile.avatarUrl} size={44} />
                    <div className="min-w-0">
                      <p className="truncate font-semibold">{profile.name}</p>
                      <p className="text-xs text-muted">
                        {profile.isKids ? 'Kids profile' : 'Watching now'}
                      </p>
                    </div>
                  </div>
                )}
                <MenuItem icon={<Users />} onSelect={switchProfile}>
                  Switch Profile
                </MenuItem>
                <DM.Separator className="my-1 h-px bg-line" />
                <MenuItem icon={<ChartColumn />} onSelect={() => navigate('/stats')}>
                  Profile Statistics
                </MenuItem>
                {party && (
                  <MenuItem
                    icon={<PartyPopper />}
                    onSelect={() => void party.onSelect()}
                    disabled={party.busy}
                    end={
                      party.members > 0 && (
                        <span className="ml-auto grid h-5 min-w-5 place-items-center rounded-full bg-pink px-1.5 text-[11px] font-bold leading-none text-on-pink">
                          {party.members}
                        </span>
                      )
                    }
                  >
                    {party.label}
                  </MenuItem>
                )}
                {!profile?.isKids && (
                  <MenuItem icon={<Puzzle />} onSelect={() => navigate('/settings/addons')}>
                    Addons
                  </MenuItem>
                )}
                <MenuItem icon={<Download />} onSelect={() => navigate('/downloads')}>
                  Downloads
                </MenuItem>
                <DM.Separator className="my-1 h-px bg-line" />
                <MenuItem icon={<Settings />} onSelect={() => navigate('/settings')}>
                  Settings
                </MenuItem>
                <MenuItem icon={<LogOut />} onSelect={signOut} danger>
                  Sign Out
                </MenuItem>
              </DM.Content>
            </DM.Portal>
          </DM.Root>
        </div>
      </div>
    </header>
  );
}

function MenuItem({
  icon,
  children,
  onSelect,
  danger,
  disabled,
  end,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Something at the end of the row, such as a count. */
  end?: React.ReactNode;
}) {
  return (
    <DM.Item
      onSelect={onSelect}
      disabled={disabled}
      className={cn(
        'flex cursor-pointer items-center gap-3 rounded-xl px-2.5 py-2.5 text-[14px] outline-none transition-colors data-[disabled]:opacity-50 [&_svg]:h-4.5 [&_svg]:w-4.5',
        danger
          ? 'text-danger data-[highlighted]:bg-danger/10 [&_svg]:text-danger'
          : 'text-text/85 data-[highlighted]:bg-white/8 data-[highlighted]:text-text [&_svg]:text-muted data-[highlighted]:[&_svg]:text-pink',
      )}
    >
      {icon}
      {children}
      {end}
    </DM.Item>
  );
}
